import { BlockLayout } from '@nucleus/types/web';
import { BuildSectionConfig, ExtractPayload, SectionTypeEnum } from '@nucleus/types/web/section-data';

type LayoutBuilder<T extends SectionTypeEnum> = (config: BuildSectionConfig) => Partial<ExtractPayload<T>>;
type WrapFn<T extends SectionTypeEnum> = (
  config: BuildSectionConfig,
  layoutPayload: Partial<ExtractPayload<T>>
) => ExtractPayload<T>;

export class SectionDefaultBuilder<T extends SectionTypeEnum> {
  private layouts: Partial<Record<BlockLayout, LayoutBuilder<T>>> = {};
  private wrapFn: WrapFn<T> | undefined = undefined;
  private staticFn: ((config: BuildSectionConfig) => ExtractPayload<T>) | undefined = undefined;

  addLayout(id: BlockLayout, builder: LayoutBuilder<T>): this {
    this.layouts[id] = builder;
    return this;
  }

  setWrapper(wrap: WrapFn<T>): this {
    this.wrapFn = wrap;
    return this;
  }

  setStatic(fn: (config: BuildSectionConfig) => ExtractPayload<T>): this {
    this.staticFn = fn;
    return this;
  }

  build(): (config: BuildSectionConfig) => ExtractPayload<T> {
    const layouts = this.layouts;
    const wrapFn = this.wrapFn;
    const staticFn = this.staticFn;

    return (config: BuildSectionConfig): ExtractPayload<T> => {
      if (staticFn !== undefined) {
        return staticFn(config);
      }

      const layoutBuilder = layouts[config.layout];
      if (layoutBuilder === undefined) {
        throw new Error(`No default defined for layout: ${config.layout}`);
      }

      const layoutPayload = layoutBuilder(config);

      if (wrapFn !== undefined) {
        return wrapFn(config, layoutPayload);
      }

      return layoutPayload as ExtractPayload<T>;
    };
  }
}
